import React, { useEffect, useState } from 'react';
import { Calendar, AlertCircle, Search, ArrowLeft, Clock, Info } from 'lucide-react';
import { Link } from 'react-router-dom';

export default function Announcements() {
  const [announcements, setAnnouncements] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');

  useEffect(() => {
    fetch('https://api.dev.auxo.dk/public/content/announcements')
      .then((res) => {
        if (!res.ok) throw new Error('Failed to fetch');
        return res.json();
      })
      .then((data) => {
        setAnnouncements(data || []);
        setLoading(false);
      })
      .catch((err) => {
        console.error('Error fetching announcements:', err);
        setError(true);
        setLoading(false);
      });
  }, []);

  const fallbackAnnouncements = [
    {
      id: '1',
      title: { da: 'Nye Vandure' },
      date: { da: '12. maj 2026' },
      content: { da: '<div class="grid grid-cols-1 md:grid-cols-[1fr_auto] gap-6 items-center"><div><p>Vandværket vil begynde installation af nye vandure den 26/5 2026.</p><p>Installationen forventes at løbe over de næste 2 måneder.</p><p>Der vil tilgå hver enkelt forbruger meddelelse om nøjagtig tidspunkt for installation, samtidigt mulighed for downloading af APP fra <a href="https://klimaid.dk/" target="_blank" rel="noopener noreferrer">KlimaID</a> som vil give hver enkelt forbruger mulighed for at følge sit vandforbrug, og modtage arlarmer ved brud på instalationen.</p></div><div class="flex justify-center md:justify-end"><img src="https://assets.auxodev.dk/cdn-cgi/image/width=400,quality=85,format=auto/assets/74070791-98c6-4435-9dcf-71cab0b4761c/image.png" alt="KlimaID" class="rounded-xl border border-slate-200 shadow-md w-full max-w-[170px]" /></div></div>' },
      important: false,
    },
    {
      id: '2',
      title: { da: 'Generalforsamling 2026 afholdt' },
      date: { da: '27. februar 2026' },
      content: { da: '<p>Den årlige generalforsamling blev afholdt torsdag den 26. februar 2026 i Grindsted Forsamlingshus.</p><p>Referat og formandens beretning er nu tilgængelige under mødereferater. Tak til alle fremmødte for en god og konstruktiv aften.</p>' },
      important: false,
    },
    {
      id: '3',
      title: { da: 'Planlagt ledningsarbejde på Smedevej' },
      date: { da: '15. april 2026' },
      content: { da: '<p>I forbindelse med renovering af hovedledningen vil der være kortvarige afbrydelser af vandforsyningen på Smedevej tirsdag den 21. april mellem kl. 09:00 og 12:00.</p><p>Vi anbefaler at tappe vand på forhånd. Når vandet kommer tilbage, kan det være kortvarigt misfarvet. Lad i så fald vandet løbe, til det er klart.</p>' },
      important: true,
    }
  ];

  const displayAnnouncements = error || announcements.length === 0 ? fallbackAnnouncements : announcements;

  // Sorter meddelelser efter dato, så de nyeste ligger først
  const parseDanishDate = (dateStr) => {
    if (!dateStr) return new Date(0);
    const cleanStr = dateStr.toLowerCase().replace('.', '').trim();
    const parts = cleanStr.split(/\s+/);
    if (parts.length < 3) return new Date(0);

    const day = parseInt(parts[0], 10);
    const monthStr = parts[1];
    const year = parseInt(parts[2], 10);

    const months = {
      januar: 0,
      februar: 1,
      marts: 2,
      april: 3,
      maj: 4,
      juni: 5,
      juli: 6,
      august: 7,
      september: 8,
      oktober: 9,
      november: 10,
      december: 11
    };

    const month = months[monthStr] !== undefined ? months[monthStr] : 0;
    return new Date(year, month, day);
  };

  const sortedAnnouncements = [...displayAnnouncements].sort((a, b) => {
    const dateA = parseDanishDate(a.date?.da);
    const dateB = parseDanishDate(b.date?.da);
    return dateB - dateA;
  });

  const filteredAnnouncements = sortedAnnouncements.filter((item) =>
    item.title.da.toLowerCase().includes(searchTerm.toLowerCase()) ||
    item.content.da.toLowerCase().includes(searchTerm.toLowerCase())
  );

  return (
    <div className="min-h-screen bg-slate-50 py-12 sm:py-16">
      <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
        {/* Breadcrumb */}
        <div className="mb-6">
          <Link to="/" className="inline-flex items-center gap-1.5 text-sm font-semibold text-black hover:text-slate-600 transition">
            <ArrowLeft className="h-4 w-4" />
            Tilbage til forsiden
          </Link>
        </div>

        {/* Header */}
        <div className="border-b border-slate-200 pb-8">
          <h1 className="text-3xl font-extrabold tracking-tight text-slate-900 sm:text-4xl">
            Meddelelser & Nyheder
          </h1>
          <p className="mt-3 text-lg text-slate-500">
            Her finder du vigtige meddelelser om vandforsyningen, driftsforstyrrelser, generalforsamlinger og andre nyheder fra Grindsted Vandværk.
          </p>
        </div>

        {/* Search Bar */}
        <div className="mt-8 flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
          <div className="relative flex-1 max-w-md">
            <Search className="absolute top-3.5 left-4 h-5 w-5 text-slate-400" />
            <input
              type="text"
              placeholder="Søg i meddelelser..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full rounded-xl border border-slate-200 bg-white py-3 pr-4 pl-12 text-sm font-medium text-slate-900 placeholder-slate-400 focus:border-blue-500 focus:ring-2 focus:ring-blue-500/20 focus:outline-none transition-all"
            />
          </div>
          <div className="flex items-center gap-2 text-xs font-semibold text-slate-500 bg-slate-100 px-3 py-1.5 rounded-lg self-start sm:self-auto">
            <Info className="h-4 w-4 text-slate-400" />
            Viser {filteredAnnouncements.length} af {displayAnnouncements.length} meddelelser
          </div>
        </div>

        {/* Announcements List */}
        <div className="mt-8 space-y-6">
          {loading ? (
            <div className="space-y-6">
              {[1, 2, 3].map((i) => (
                <div key={i} className="animate-pulse rounded-2xl border border-slate-200 bg-white p-6 sm:p-8">
                  <div className="h-4 w-1/4 rounded bg-slate-200" />
                  <div className="mt-4 h-7 w-2/3 rounded bg-slate-200" />
                  <div className="mt-3 space-y-2">
                    <div className="h-4 w-full rounded bg-slate-200" />
                    <div className="h-4 w-5/6 rounded bg-slate-200" />
                  </div>
                </div>
              ))}
            </div>
          ) : filteredAnnouncements.length === 0 ? (
            <div className="rounded-2xl border border-dashed border-slate-300 bg-white p-12 text-center">
              <Search className="mx-auto h-12 w-12 text-slate-400" />
              <h3 className="mt-4 text-lg font-bold text-slate-900">Ingen meddelelser fundet</h3>
              <p className="mt-2 text-sm text-slate-500">
                Prøv at søge efter noget andet eller nulstil søgefeltet.
              </p>
              <button
                onClick={() => setSearchTerm('')}
                className="mt-4 inline-flex items-center gap-1.5 rounded-lg bg-blue-600 px-4 py-2 text-xs font-bold text-white hover:bg-blue-500 transition"
              >
                Nulstil søgning
              </button>
            </div>
          ) : (
            filteredAnnouncements.map((item) => (
              <article
                key={item.id}
                className="relative rounded-2xl border border-slate-200/80 p-6 sm:p-8 transition-all bg-white shadow-sm hover:shadow-md"
              >
                {/* Header info */}
                <div className="flex flex-wrap items-center justify-between gap-4">
                  <div className="flex items-center gap-2.5 text-xs font-semibold text-slate-500">
                    <Calendar className="h-4 w-4 text-blue-500" />
                    {item.date.da}
                  </div>
                </div>

                {/* Title */}
                <h2 className="mt-4 text-xl font-extrabold text-slate-900 sm:text-2xl">
                  {item.title.da}
                </h2>

                {/* Content */}
                <div
                  className="mt-4 text-slate-600 leading-relaxed prose prose-slate max-w-none flow-root [&_a]:text-blue-600 [&_a]:underline [&_a]:font-bold hover:[&_a]:text-blue-800 transition-colors"
                  dangerouslySetInnerHTML={{ __html: item.content.da }}
                />

                {/* Footer info */}
                <div className="mt-6 border-t border-slate-100 pt-4 flex items-center justify-between text-xs text-slate-400">
                  <span className="flex items-center gap-1">
                    <Clock className="h-3.5 w-3.5" />
                    Udgivet af bestyrelsen
                  </span>
                  <span>Grindsted Vandværk</span>
                </div>
              </article>
            ))
          )}
        </div>
      </div>
    </div>
  );
}
